import { API_URL } from '../config';
import React, { useState, useEffect, useRef, useCallback, useMemo, ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { io, Socket } from 'socket.io-client';
import { sessionsApi, Session, messagesApi, ChatConversation, MessageWithContact } from '../api/api';

const PAGE_SIZE = 30;
const CHAT_LIST_PAGE_SIZE = 40;
const STATS_CONCURRENCY = 5; // máximo de requisições simultâneas de stats

// Barras estáticas de waveform para simular o visual do WhatsApp
const WAVEFORM_BARS = [3,5,8,4,9,6,3,7,5,8,4,6,9,3,5,7,4,8,6,3,7,5,9,4,6,8,3,5,7,4];

function AudioPlayer({ src, isSent, photo }: { src: string; isSent: boolean; photo?: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    playing ? audio.pause() : audio.play();
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (audioRef.current && duration > 0) audioRef.current.currentTime = ratio * duration;
  };

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    return `${m}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? currentTime / duration : 0;
  const activeBars = Math.round(progress * WAVEFORM_BARS.length);

  return (
    <div className={`flex items-center gap-2.5 mb-2 px-2 py-2 rounded-xl w-full`} style={{ minWidth: 230, maxWidth: 320 }}>
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
        onEnded={() => { setPlaying(false); setCurrentTime(0); }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      {/* Avatar */}
      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden shadow-sm ${photo ? '' : isSent ? 'bg-[#00a884]' : 'bg-gray-400'}`}>
        {photo ? (
          <img src={photo} alt="" className="w-full h-full object-cover" />
        ) : (
          <svg className="w-5 h-5 text-gray-900" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 14c1.33 0 4 .67 4 2v1H8v-1c0-1.33 2.67-2 4-2zm0-2a3 3 0 100-6 3 3 0 000 6z"/>
          </svg>
        )}
      </div>
      {/* Controls */}
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {/* Play/Pause */}
          <button
            type="button"
            onClick={togglePlay}
            className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${isSent ? 'text-[#00a884] hover:bg-[#00a884]/10' : 'text-gray-600 hover:bg-gray-200'}`}
          >
            {playing ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>
          {/* Waveform bars */}
          <div
            className="flex items-center gap-[2px] flex-1 cursor-pointer h-8"
            onClick={handleSeek}
            title="Clique para navegar"
          >
            {WAVEFORM_BARS.map((h, i) => (
              <div
                key={i}
                className="rounded-full flex-shrink-0 transition-colors"
                style={{
                  width: 3,
                  height: `${(h / 9) * 100}%`,
                  minHeight: 3,
                  backgroundColor: i < activeBars
                    ? (isSent ? '#00a884' : '#53bdeb')
                    : (isSent ? '#93c9bb' : '#c5c5c5'),
                }}
              />
            ))}
          </div>
        </div>
        {/* Duration */}
        <span className="text-[11px] text-gray-500 ml-10 leading-none">
          {fmt(playing || currentTime > 0 ? currentTime : duration)}
        </span>
      </div>
    </div>
  );
}

interface Props {
  createModalOpen: boolean;
  setCreateModalOpen: (v: boolean) => void;
  maxSessoes: number;
  isAdmin: boolean;
  onIrParaPagamento?: () => void;
}

interface SessionStats {
  totalMessages: number;
  totalContacts: number;
}

interface LimitModalData {
  quantidadeAtual: number;
  limitePlano: number;
  planoAtual: string;
}

export default function SessionsPage({ createModalOpen, setCreateModalOpen, maxSessoes, isAdmin, onIrParaPagamento }: Props) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionLimite, setNewSessionLimite] = useState<string>('');
  const [limitModalData, setLimitModalData] = useState<LimitModalData | null>(null);
  const [loading, setLoading] = useState(false);
  const [qrcodes, setQrcodes] = useState<Record<number, string>>({});
  const [selectedQr, setSelectedQr] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'connected' | 'disconnected'>('all');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState<Record<number, SessionStats>>({});
  const [settingsMenuOpen, setSettingsMenuOpen] = useState<number | null>(null);
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingLimite, setEditingLimite] = useState(0);

  // Chat modal states
  const [messagesModalSessionId, setMessagesModalSessionId] = useState<number | null>(null);
  const [sessionConversations, setSessionConversations] = useState<Record<number, ChatConversation[]>>({});
  const [messagesLoading, setMessagesLoading] = useState<Set<number>>(new Set());
  const [chatMessages, setChatMessages] = useState<Record<string, MessageWithContact[]>>({});
  const [chatMessagesLoading, setChatMessagesLoading] = useState<Set<string>>(new Set());
  const [selectedChatPhone, setSelectedChatPhone] = useState<string | null>(null);
  const [chatSearch, setChatSearch] = useState('');
  const [chatFilter, setChatFilter] = useState<'all' | 'contacts' | 'groups'>('all');
  const [chatListPage, setChatListPage] = useState(0);
  const [replyingTo, setReplyingTo] = useState<{ id: number; message: string } | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [mediaToSend, setMediaToSend] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [profilePics, setProfilePics] = useState<Record<number, string | null>>({});
  const profilePicsLoadedRef = useRef<Set<number>>(new Set());
  const [profilePicModal, setProfilePicModal] = useState<{ sessionId: number; currentPhoto: string | null } | null>(null);
  const [profilePicLoading, setProfilePicLoading] = useState(false);
  const profilePicInputRef = useRef<HTMLInputElement>(null);

  // Foto em massa
  const [bulkPhotoPreview, setBulkPhotoPreview] = useState<string | null>(null);
  const [bulkPhotoLoading, setBulkPhotoLoading] = useState(false);
  const [bulkPhotoResult, setBulkPhotoResult] = useState<{ success: number; failed: number; errors: string[] } | null>(null);
  const bulkPhotoInputRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [qrTimedOut, setQrTimedOut] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const listenedIdsRef = useRef<Set<number>>(new Set());
  const statsQueueRef = useRef<number[]>([]);
  const statsInFlightRef = useRef(0);
  const loadedOrQueuedRef = useRef<Set<number>>(new Set());

  const loadSessions = useCallback(async () => {
    try { const { data } = await sessionsApi.list(); setSessions(data); } catch {}
  }, []);

  const loadSessionStats = useCallback(async (id: number) => {
    try {
      const { data } = await sessionsApi.getStats(id);
      setStats((prev) => ({ ...prev, [id]: data }));
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
    }
  }, []);

  const loadSessionMessages = useCallback(async (id: number) => {
    setMessagesLoading((prev) => new Set(prev).add(id));
    try {
      const { data } = await messagesApi.getSessionConversations(id);
      setSessionConversations((prev) => ({ ...prev, [id]: data }));
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    } finally {
      setMessagesLoading((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, []);

  const loadConversationMessages = useCallback(async (sessionId: number, phone: string) => {
    const key = `${sessionId}:${phone}`;
    setChatMessagesLoading((prev) => new Set(prev).add(key));
    try {
      const { data } = await messagesApi.getConversationMessages(sessionId, phone);
      setChatMessages((prev) => ({ ...prev, [key]: data }));
    } catch (error) {
      console.error('Erro ao carregar conversa:', error);
    } finally {
      setChatMessagesLoading((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page on filter change
  useEffect(() => { setPage(0); }, [filter]);

  useEffect(() => {
    setChatListPage(0);
  }, [chatFilter, chatSearch, messagesModalSessionId]);

  // Queue-based stats loading (max STATS_CONCURRENCY concurrent requests)
  const processStatsQueue = useCallback(() => {
    while (statsQueueRef.current.length > 0 && statsInFlightRef.current < STATS_CONCURRENCY) {
      const id = statsQueueRef.current.shift()!;
      statsInFlightRef.current++;
      loadSessionStats(id).finally(() => {
        statsInFlightRef.current--;
        processStatsQueue();
      });
    }
  }, [loadSessionStats]);

  useEffect(() => {
    let added = false;
    sessions.forEach((s) => {
      if (!loadedOrQueuedRef.current.has(s.id)) {
        loadedOrQueuedRef.current.add(s.id);
        statsQueueRef.current.push(s.id);
        added = true;
      }
    });
    if (added) processStatsQueue();
  }, [sessions, processStatsQueue]);

  // Carrega foto de perfil das sessões conectadas visíveis (lazy)
  useEffect(() => {
    sessions.forEach((s) => {
      if (s.status !== 'connected') return;
      if (profilePicsLoadedRef.current.has(s.id)) return;
      profilePicsLoadedRef.current.add(s.id);
      sessionsApi.getProfilePicture(s.id)
        .then(({ data }) => setProfilePics(prev => ({ ...prev, [s.id]: data.photo })))
        .catch(() => setProfilePics(prev => ({ ...prev, [s.id]: null })));
    });
  }, [sessions]);

  // Cria o socket uma única vez
  useEffect(() => {
    const socket = io(API_URL, { transports: ['websocket'] });
    socketRef.current = socket;
    return () => {
      socket.disconnect();
      socketRef.current = null;
      listenedIdsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (messagesModalSessionId === null || !selectedChatPhone) return;
    loadConversationMessages(messagesModalSessionId, selectedChatPhone);
  }, [messagesModalSessionId, selectedChatPhone, loadConversationMessages]);

  // Timeout do modal de QR: se após 45s ainda não chegou o QR, mostra erro
  useEffect(() => {
    if (!selectedQr || qrcodes[selectedQr]) { setQrTimedOut(false); return; }
    setQrTimedOut(false);
    const t = setTimeout(() => setQrTimedOut(true), 45000);
    return () => clearTimeout(t);
  }, [selectedQr, qrcodes]);

  // Registra listeners apenas para IDs novos
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    sessions.forEach((session) => {
      if (listenedIdsRef.current.has(session.id)) return;
      listenedIdsRef.current.add(session.id);

      socket.on(`qrcode:${session.id}`, ({ qrcode }: { qrcode: string }) => {
        setQrcodes((prev) => ({ ...prev, [session.id]: qrcode }));
      });

      socket.on(`session:${session.id}`, ({ status, numero }: { status: string; numero?: string }) => {
        setSessions((prev) =>
          prev.map((s) => s.id === session.id ? { ...s, status: status as any, numeroWhatsapp: numero || s.numeroWhatsapp } : s)
        );
        if (status === 'connected') {
          setQrcodes((prev) => { const n = { ...prev }; delete n[session.id]; return n; });
          setTimeout(() => {
            loadSessionStats(session.id);
          }, 500);
        }
      });
    });
  }, [sessions, loadSessionStats]);

  const handleCreate = async () => {
    if (!newSessionName.trim()) return;
    setLoading(true);
    try {
      const limiteVal = parseInt(newSessionLimite) || 0;
      await sessionsApi.create(newSessionName.trim(), limiteVal > 0 ? limiteVal : undefined);
      setNewSessionName('');
      setNewSessionLimite('');
      setCreateModalOpen(false);
      await loadSessions();
      showToast('Sessão criada com sucesso');
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.code === 'LIMITE_SESSOES_ATINGIDO') {
        setCreateModalOpen(false);
        setNewSessionName('');
        setNewSessionLimite('');
        setLimitModalData({
          quantidadeAtual: data.quantidade_atual ?? 0,
          limitePlano: data.limite_plano ?? 0,
          planoAtual: data.plano_atual ?? 'Plano Atual',
        });
      } else {
        alert('Erro ao criar sessão');
      }
    } finally { setLoading(false); }
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedChatPhone || messagesModalSessionId === null) return;

    const messageText = messageInput;
    const replyData = replyingTo;

    setMessageInput('');
    setReplyingTo(null);

    try {
      const { data } = await messagesApi.sendSessionMessage(messagesModalSessionId, {
        phone: selectedChatPhone,
        message: messageText,
        replyToMessageId: replyData?.id,
      });
      const key = `${messagesModalSessionId}:${selectedChatPhone}`;
      setChatMessages((prev) => ({
        ...prev,
        [key]: [...(prev[key] || []), data],
      }));
      setSessionConversations((prev) => ({
        ...prev,
        [messagesModalSessionId]: (prev[messagesModalSessionId] || []).map((conversation) =>
          conversation.phone === selectedChatPhone
            ? { ...conversation, lastMessage: data.message, lastMessageTime: data.timestamp, messageCount: conversation.messageCount + 1, hasMessages: true }
            : conversation,
        ),
      }));
      showToast('Mensagem enviada com sucesso');
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      showToast('Erro ao enviar mensagem');
      setMessageInput(messageText);
      setReplyingTo(replyData || null);
    }
  };

  // Auto-scroll para a última mensagem sempre que chatMessages ou a conversa selecionada mudar
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, selectedChatPhone]);

  const handleMediaSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (mediaPreview) URL.revokeObjectURL(mediaPreview);
    setMediaToSend(file);
    setMediaPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleSendMedia = async () => {
    if (!mediaToSend || !selectedChatPhone || messagesModalSessionId === null) return;
    const file = mediaToSend;
    const caption = messageInput;
    const preview = mediaPreview;
    setMediaToSend(null);
    setMediaPreview(null);
    setMessageInput('');
    try {
      const { data } = await messagesApi.sendSessionMedia(messagesModalSessionId, {
        phone: selectedChatPhone,
        file,
        message: caption || undefined,
      });
      const key = `${messagesModalSessionId}:${selectedChatPhone}`;
      setChatMessages((prev) => ({ ...prev, [key]: [...(prev[key] || []), data] }));
      setSessionConversations((prev) => ({
        ...prev,
        [messagesModalSessionId]: (prev[messagesModalSessionId] || []).map((c) =>
          c.phone === selectedChatPhone
            ? { ...c, lastMessage: data.message, lastMessageTime: data.timestamp, messageCount: c.messageCount + 1, hasMessages: true }
            : c,
        ),
      }));
      if (preview) URL.revokeObjectURL(preview);
      showToast('Mídia enviada com sucesso');
    } catch (error) {
      console.error('Erro ao enviar mídia:', error);
      showToast('Erro ao enviar mídia');
      setMediaToSend(file);
      setMediaPreview(preview);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await sessionsApi.delete(id);
      await loadSessions();
      showToast('Sessão excluída com sucesso');
    } catch { alert('Erro ao excluir'); }
    finally { setDeleteConfirmId(null); }
  };

  const handleReconnect = async (id: number) => {
    try { await sessionsApi.reconnect(id); } catch { alert('Erro ao reconectar'); }
  };

  const handleShowQr = async (id: number) => {
    setSelectedQr(id);
    if (qrcodes[id]) return;
    try {
      const { data } = await sessionsApi.getQrCode(id);
      if (data.qrcode) { setQrcodes((prev) => ({ ...prev, [id]: data.qrcode! })); }
    } catch {}
  };

  const handleRenewSession = async (id: number) => {
    setSettingsMenuOpen(null);
    try {
      await sessionsApi.reconnect(id);
      showToast('Sessão renovada com sucesso');
    } catch {
      alert('Erro ao renovar sessão');
    }
  };

  const handleDisconnect = async (id: number) => {
    setSettingsMenuOpen(null);
    try {
      await sessionsApi.disconnect(id);
      await loadSessions();
      showToast('Sessão desconectada com sucesso');
    } catch {
      alert('Erro ao desconectar sessão');
    }
  };

  const handleEditSession = (id: number, currentName: string, currentLimite: number) => {
    setEditingSessionId(id);
    setEditingName(currentName);
    setEditingLimite(currentLimite ?? 0);
    setSettingsMenuOpen(null);
  };

  const handleSaveName = async () => {
    if (!editingSessionId || !editingName.trim()) return;
    try {
      await sessionsApi.update(editingSessionId, {
        nome_sessao: editingName.trim(),
        limite_disparos: editingLimite,
      });
      await loadSessions();
      setEditingSessionId(null);
      setEditingName('');
      setEditingLimite(0);
      showToast('Sessão atualizada com sucesso');
    } catch {
      alert('Erro ao atualizar sessão');
    }
  };

  const handleOpenProfilePic = async (id: number) => {
    setSettingsMenuOpen(null);
    // Usa foto já carregada no card, se disponível
    const cached = profilePics[id];
    setProfilePicModal({ sessionId: id, currentPhoto: cached ?? null });
    try {
      const { data } = await sessionsApi.getProfilePicture(id);
      setProfilePicModal({ sessionId: id, currentPhoto: data.photo });
      if (data.photo) setProfilePics(prev => ({ ...prev, [id]: data.photo }));
    } catch {}
  };

  const handleProfilePicUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profilePicModal) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target?.result as string;
      setProfilePicLoading(true);
      try {
        await sessionsApi.updateProfilePicture(profilePicModal.sessionId, base64);
        setProfilePicModal(prev => prev ? { ...prev, currentPhoto: base64 } : null);
        setProfilePics(prev => ({ ...prev, [profilePicModal.sessionId]: base64 }));
        showToast('Foto de perfil atualizada!');
      } catch {
        showToast('Erro ao atualizar foto');
      } finally {
        setProfilePicLoading(false);
        if (profilePicInputRef.current) profilePicInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const handleBulkPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setBulkPhotoPreview(ev.target?.result as string);
      setBulkPhotoResult(null);
    };
    reader.readAsDataURL(file);
    if (bulkPhotoInputRef.current) bulkPhotoInputRef.current.value = '';
  };

  const handleBulkPhotoConfirm = async () => {
    if (!bulkPhotoPreview) return;
    setBulkPhotoLoading(true);
    try {
      const { data } = await sessionsApi.bulkUpdateProfilePicture(bulkPhotoPreview);
      setBulkPhotoResult(data);
      // Limpa fotos em cache para forçar reload
      profilePicsLoadedRef.current.clear();
      setProfilePics({});
    } catch {
      setBulkPhotoResult({ success: 0, failed: connectedCount, errors: ['Erro ao aplicar fotos'] });
    } finally {
      setBulkPhotoLoading(false);
    }
  };

  const connectedCount = sessions.filter(s => s.status === 'connected').length;

  const filteredSessions = useMemo(() => {
    const limited = (!isAdmin && maxSessoes > 0) ? sessions.slice(0, maxSessoes) : sessions;
    return limited.filter(s => {
      const matchStatus = filter === 'all' || (filter === 'connected' ? s.status === 'connected' : s.status !== 'connected');
      const q = debouncedSearch.trim().toLowerCase();
      const matchSearch = !q ||
        s.nomeSessao.toLowerCase().includes(q) ||
        (s.numeroWhatsapp ?? '').includes(debouncedSearch.replace(/\D/g, '') || debouncedSearch);
      return matchStatus && matchSearch;
    });
  }, [sessions, filter, debouncedSearch, isAdmin, maxSessoes]);

  const totalPages = Math.ceil(filteredSessions.length / PAGE_SIZE);
  const paginatedSessions = useMemo(() =>
    filteredSessions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filteredSessions, page]);

  return (
    <div className="space-y-6 w-full" onClick={() => setSettingsMenuOpen(null)}>

      {/* Input hidden para foto em massa */}
      <input
        ref={bulkPhotoInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleBulkPhotoSelect}
      />

      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg transition-opacity hover:opacity-80 flex-shrink-0"
          style={{ background: '#22C55E' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          Conectar WhatsApp
        </button>
        <button
          type="button"
          onClick={() => { setBulkPhotoPreview(null); setBulkPhotoResult(null); bulkPhotoInputRef.current?.click(); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg transition-all hover:bg-blue-700 flex-shrink-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Alterar Fotos em Massa
        </button>
        <div className="flex items-center gap-2 border rounded-lg px-3 py-2 w-64 bg-white border border-gray-200">
          <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Nome ou telefone..."
            className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
          />
        </div>
        {/* Filtros */}
        <div className="flex items-center gap-1 border rounded-lg p-1 bg-white border border-gray-200">
          {([
            { key: 'all', label: 'Todas', dot: null },
            { key: 'connected', label: 'Conectadas', dot: 'bg-emerald-500' },
            { key: 'disconnected', label: 'Desconectadas', dot: 'bg-red-400' },
          ] as const).map(({ key, label, dot }) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                filter === key ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Cards */}
      {filteredSessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <p className="text-gray-400 text-sm">
            {sessions.length === 0 ? 'Nenhuma sessão criada' : 'Nenhuma sessão encontrada'}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
            {paginatedSessions.map((session) => {
            const isConnected = session.status === 'connected';
            const isQrcode = session.status === 'qrcode';
            const isConnecting = session.status === 'connecting';
            const isDisconnected = session.status === 'disconnected';
            const isDisparando = (session.statusDisparo ?? 'idle') === 'disparando';
            const isLimiteAtingido = (session.statusDisparo ?? 'idle') === 'limite_atingido';

            const isPausado = isConnected && !isDisparando && !isLimiteAtingido;

            const statusLabel = isLimiteAtingido
              ? 'Limite de Disparo Atingido'
              : isDisparando
              ? 'Disparando'
              : isQrcode
              ? 'Aguard. QR'
              : isConnecting
              ? 'Conectando...'
              : isDisconnected
              ? 'Desconectado'
              : 'Conectado';

            const disparoStatusLabel = isLimiteAtingido
              ? 'Limite de Disparo Atingido'
              : isDisparando
              ? 'Disparando'
              : isPausado
              ? 'Pausado'
              : null;

            const statusBgColor = isDisconnected
              ? 'bg-[rgba(239,68,68,0.2)]'
              : isConnecting || isQrcode
              ? 'bg-[rgba(245,158,11,0.2)]'
              : 'bg-[rgba(34,197,94,0.2)]';

            const statusTextColor = isDisconnected
              ? 'text-red-600'
              : isConnecting || isQrcode
              ? 'text-yellow-700'
              : 'text-emerald-700';

            const disparoBgColor = isLimiteAtingido
              ? 'bg-[rgba(249,115,22,0.2)]'
              : isDisparando
              ? 'bg-[rgba(59,130,246,0.2)]'
              : 'bg-[rgba(245,158,11,0.2)]';

            const disparoTextColor = isLimiteAtingido
              ? 'text-orange-700'
              : isDisparando
              ? 'text-blue-700'
              : 'text-yellow-700';

            return (
              <div key={session.id} className="border rounded-lg p-5 hover:shadow-md transition-all relative bg-white border border-gray-200">
                {/* Header com settings */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden bg-gray-100 border border-gray-200">
                      {profilePics[session.id] ? (
                        <img src={profilePics[session.id]!} alt="Foto" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-base font-semibold text-gray-900 truncate">{session.nomeSessao}</p>
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      title="Configurações"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSettingsMenuOpen(settingsMenuOpen === session.id ? null : session.id);
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all flex-shrink-0"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                    {settingsMenuOpen === session.id && (
                      <div className="absolute right-0 mt-2 w-48 border rounded-lg shadow-lg z-10 bg-white border border-gray-200" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSession(session.id, session.nomeSessao, session.limiteDisparos ?? 0);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 first:rounded-t-lg transition-all border-b border-gray-100"
                        >
                          Editar Nome
                        </button>
                        {isConnected && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenProfilePic(session.id);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-all border-b border-gray-100"
                          >
                            Foto de Perfil
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRenewSession(session.id);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-all border-b border-gray-100"
                        >
                          Renovar Sessão
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDisconnect(session.id);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 last:rounded-b-lg transition-all"
                        >
                          Desconectar
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Número */}
                <div className="mb-3 flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-500 font-mono select-none">
                    {session.numeroWhatsapp ? `+${session.numeroWhatsapp}` : '—'}
                  </span>
                  {session.limiteDisparos > 0 && (
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                      {session.limiteDisparos} disp. máx.
                    </span>
                  )}
                </div>

                {/* Status */}
                <div className="mb-4 flex items-center gap-2 flex-wrap">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${statusBgColor} ${statusTextColor}`}>
                    {statusLabel}
                  </span>
                  {disparoStatusLabel && (
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${disparoBgColor} ${disparoTextColor}`}>
                      {disparoStatusLabel}
                    </span>
                  )}
                </div>

                {/* Contadores */}
                <div className="flex gap-4 mb-4 text-center items-center">
                  <div className="flex gap-4 w-full">
                    <div className="flex-1">
                      <p className="text-lg font-semibold text-gray-900">{session.totalDisparos ?? 0}</p>
                      <p className="text-xs text-gray-500">disparados</p>
                    </div>
                    <div className="w-px bg-gray-100" />
                    <div className="flex-1">
                      <p className="text-lg font-semibold text-gray-900">{stats[session.id]?.totalContacts ?? session.totalContacts ?? 0}</p>
                      <p className="text-xs text-gray-500">contatos</p>
                    </div>
                  </div>
                </div>

                {/* Botões */}
                <div className="flex gap-2 items-center">
                  {isDisconnected && session.numeroWhatsapp && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReconnect(session.id);
                      }}
                      className="flex-1 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold border border-gray-200 transition-all"
                    >
                      Reconectar
                    </button>
                  )}
                  {(isQrcode || isConnecting || (isDisconnected && !session.numeroWhatsapp)) && (
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (isDisconnected && !session.numeroWhatsapp) {
                          setSelectedQr(session.id);
                          try { await sessionsApi.reconnect(session.id); } catch {}
                        } else {
                          handleShowQr(session.id);
                        }
                      }}
                      className="flex-1 px-3 py-2 rounded-lg bg-[rgba(245,158,11,0.2)] hover:bg-amber-200 text-yellow-700 text-xs font-semibold border border-[rgba(245,158,11,0.3)] transition-all flex items-center justify-center gap-2"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                      QR Code
                    </button>
                  )}
                  <div className="flex-1" />
                  {isConnected && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        loadSessionMessages(session.id);
                        setMessagesModalSessionId(session.id);
                        setSelectedChatPhone(null);
                        setReplyingTo(null);
                        setMessageInput('');
                      }}
                      title="Ver mensagens e contatos"
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-blue-700 hover:bg-blue-50 transition-all flex-shrink-0"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v9a2 2 0 01-2 2h-4l-4 4v-4z" />
                      </svg>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirmId(session.id);
                    }}
                    title="Excluir sessão"
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                Anterior
              </button>
              <span className="text-xs text-gray-500">
                {page + 1} / {totalPages} — {filteredSessions.length} sessões
              </span>
              <button
                type="button"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal Foto em Massa */}
      {bulkPhotoPreview && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-2xl p-8 max-w-sm w-full shadow-xl text-center bg-white border border-gray-200">
            <h3 className="text-base font-bold text-gray-900 mb-1">Foto em Massa</h3>
            <p className="text-xs text-gray-500 mb-5">
              {bulkPhotoResult
                ? 'Atualização concluída'
                : `Aplicar esta foto em ${connectedCount} sessão${connectedCount !== 1 ? 'ões' : ''} conectada${connectedCount !== 1 ? 's' : ''}`}
            </p>

            {/* Preview */}
            <div className="flex justify-center mb-5">
              <img
                src={bulkPhotoPreview}
                alt="Preview"
                className="w-28 h-28 rounded-full object-cover border-4 border-gray-100 shadow"
              />
            </div>

            {/* Resultado */}
            {bulkPhotoResult && (
              <div className="mb-5 space-y-2">
                {bulkPhotoResult.success > 0 && (
                  <div className="flex items-center gap-2 justify-center rounded-lg px-4 py-2 bg-emerald-50 text-emerald-700">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-semibold">{bulkPhotoResult.success} atualizada{bulkPhotoResult.success !== 1 ? 's' : ''} com sucesso</span>
                  </div>
                )}
                {bulkPhotoResult.failed > 0 && (
                  <div className="rounded-lg px-4 py-2 bg-red-50 text-red-600">
                    <p className="text-sm font-semibold mb-1">{bulkPhotoResult.failed} falha{bulkPhotoResult.failed !== 1 ? 's' : ''}</p>
                    {bulkPhotoResult.errors.map((e, i) => (
                      <p key={i} className="text-xs text-red-500">{e}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {connectedCount === 0 && !bulkPhotoResult && (
              <p className="text-xs text-yellow-700 bg-yellow-50 rounded-lg px-4 py-2 mb-5">
                Nenhuma sessão conectada no momento.
              </p>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setBulkPhotoPreview(null); setBulkPhotoResult(null); }}
                className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium border border-gray-200"
              >
                {bulkPhotoResult ? 'Fechar' : 'Cancelar'}
              </button>
              {!bulkPhotoResult && (
                <button
                  type="button"
                  onClick={handleBulkPhotoConfirm}
                  disabled={bulkPhotoLoading || connectedCount === 0}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold disabled:opacity-40"
                >
                  {bulkPhotoLoading ? 'Aplicando...' : 'Aplicar em Todas'}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Foto de Perfil */}
      {profilePicModal && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-2xl p-8 max-w-sm w-full shadow-xl text-center bg-white border border-gray-200">
            <h3 className="text-base font-bold text-gray-900 mb-5">Foto de Perfil</h3>

            {/* Foto atual */}
            <div className="flex justify-center mb-5">
              {profilePicModal.currentPhoto ? (
                <img
                  src={profilePicModal.currentPhoto}
                  alt="Foto de perfil"
                  className="w-28 h-28 rounded-full object-cover border-4 border-gray-100 shadow"
                />
              ) : (
                <div className="w-28 h-28 rounded-full bg-gray-100 border-4 border-gray-200 flex items-center justify-center">
                  <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
            </div>

            <input
              ref={profilePicInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleProfilePicUpload}
            />

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setProfilePicModal(null)}
                className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium border border-gray-200"
              >
                Fechar
              </button>
              <button
                type="button"
                onClick={() => profilePicInputRef.current?.click()}
                disabled={profilePicLoading}
                className="flex-1 py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold disabled:opacity-40"
              >
                {profilePicLoading ? 'Enviando...' : 'Alterar Foto'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* QR Modal */}
      {selectedQr && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-xl w-full max-w-sm flex flex-col">
            {(() => {
              const qrSession = sessions.find(s => s.id === selectedQr);
              const isNowConnected = qrSession?.status === 'connected';
              return isNowConnected ? (
                <>
                  <div className="relative px-6 py-5 border-b border-gray-100 text-center">
                    <h3 className="text-base font-bold text-gray-900">WhatsApp Conectado!</h3>
                    <button type="button" onClick={() => setSelectedQr(null)} title="Fechar" className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="px-6 py-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-3">
                      <svg className="w-8 h-8 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{qrSession?.nomeSessao}</p>
                  </div>
                  <div className="px-6 py-5 border-t border-gray-100">
                    <button type="button" onClick={() => setSelectedQr(null)} className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md text-sm font-semibold transition-all">Fechar</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="relative px-6 py-5 border-b border-gray-100 text-center">
                    <h3 className="text-base font-bold text-gray-900">Escanear QR Code</h3>
                    <p className="text-xs text-gray-600 mt-0.5">{qrSession?.nomeSessao}</p>
                    <button type="button" onClick={() => setSelectedQr(null)} title="Fechar" className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="px-6 py-5">
                    {qrcodes[selectedQr] ? (
                      <div className="rounded-md p-3 mb-4 border bg-white border border-gray-200">
                        <img src={qrcodes[selectedQr]} alt="QR Code" className="w-full rounded" />
                      </div>
                    ) : qrTimedOut ? (
                      <div className="flex flex-col items-center justify-center py-10 mb-4">
                        <div className="w-12 h-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mb-3">
                          <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-700 font-medium mb-1">Não foi possível gerar o QR</p>
                        <p className="text-xs text-gray-400 mb-4 text-center">O WhatsApp pode estar bloqueando temporariamente. Aguarde alguns segundos e tente novamente.</p>
                        <button
                          type="button"
                          onClick={async () => {
                            setQrTimedOut(false);
                            const session = sessions.find(s => s.id === selectedQr);
                            if (!session) return;
                            try { await sessionsApi.reconnect(session.id); } catch {}
                          }}
                          className="px-4 py-2 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-gray-800 transition-all"
                        >
                          Tentar Novamente
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-10 mb-4">
                        <svg className="w-8 h-8 text-gray-400 animate-spin mb-3" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        <p className="text-sm text-gray-500">Gerando QR Code...</p>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 text-center">WhatsApp → Dispositivos vinculados → Vincular dispositivo</p>
                  </div>
                  <div className="px-6 py-5 border-t border-gray-100">
                    <button type="button" onClick={() => setSelectedQr(null)} className="w-full py-3 hover:bg-gray-100 text-gray-600 rounded-md text-sm font-medium transition-all border">Fechar</button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>,
        document.body
      )}

      {/* Modal Confirmar Exclusão */}
      {deleteConfirmId !== null && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-xl w-full max-w-sm p-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-lg bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-base font-bold text-gray-900">Excluir Sessão</h3>
              <p className="text-sm text-gray-600 mt-1">
                Tem certeza que deseja excluir a sessão <span className="text-gray-900 font-medium">"{sessions.find(s => s.id === deleteConfirmId)?.nomeSessao}"</span>? Esta ação não pode ser desfeita.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-3 hover:bg-gray-100 text-gray-600 rounded-md text-sm font-medium transition-all border"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirmId)}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white rounded-md text-sm font-semibold transition-all"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Toast sucesso */}
      {toast && createPortal(
        <div className="fixed top-0 right-0 z-[99999] flex items-center gap-3 bg-emerald-500 text-white px-5 py-4 shadow-2xl rounded-bl-xl toast-slide-in">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm font-semibold">{toast}</span>
        </div>,
        document.body
      )}

      {/* Modal Editar Nome da Sessão */}
      {editingSessionId !== null && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-xl w-full max-w-sm flex flex-col">
            <div className="relative px-6 py-5 border-b border-gray-100 text-center">
              <h3 className="text-base font-bold text-gray-900">Editar Nome da Sessão</h3>
              <button
                type="button"
                onClick={() => setEditingSessionId(null)}
                title="Fechar modal"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <input
                type="text"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                placeholder="Nome da sessão"
                autoFocus
                className="w-full border border-gray-300 rounded-md px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-500 transition-all bg-white"
              />
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <input
                    type="number"
                    min={0}
                    max={9999}
                    value={editingLimite}
                    onChange={(e) => setEditingLimite(Math.min(9999, Math.max(0, parseInt(e.target.value) || 0)))}
                    placeholder="0"
                    className="w-full border border-gray-300 rounded-md px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-500 transition-all bg-white"
                  />
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap">disparos máx.<br/><span className="text-gray-300">(0 = ilimitado)</span></span>
              </div>
            </div>
            <div className="flex gap-3 px-6 py-5 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setEditingSessionId(null)}
                className="flex-1 py-3 hover:bg-gray-100 text-gray-600 rounded-md text-sm font-medium transition-all border"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveName}
                disabled={!editingName.trim()}
                className="flex-1 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-md text-sm font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Criar Sessão */}
      {createModalOpen && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-xl w-full max-w-sm flex flex-col">
            <div className="relative px-6 py-5 border-b border-gray-100 text-center">
              <h3 className="text-base font-bold text-gray-900">Nova Sessão WhatsApp</h3>
              <button
                type="button"
                onClick={() => { setCreateModalOpen(false); setNewSessionName(''); setNewSessionLimite(''); }}
                title="Fechar modal"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-5">
              <input
                type="text"
                value={newSessionName}
                onChange={(e) => setNewSessionName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="Ex: Vendas, Suporte, Marketing..."
                autoFocus
                className="w-full border border-gray-300 rounded-md px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-500 transition-all bg-white"
              />
            </div>
            <div className="flex gap-3 px-6 py-5 border-t border-gray-100">
              <button
                type="button"
                onClick={() => { setCreateModalOpen(false); setNewSessionName(''); setNewSessionLimite(''); }}
                className="flex-1 py-3 hover:bg-gray-100 text-gray-600 rounded-md text-sm font-medium transition-all border"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={loading || !newSessionName.trim()}
                className="flex-1 py-3 rounded-md text-sm font-semibold text-white transition-opacity hover:opacity-80 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ background: '#22C55E' }}
              >
                {loading ? 'Criando...' : 'Criar'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Limite de Sessões Atingido */}
      {limitModalData && createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-2xl shadow-2xl w-full max-w-md overflow-hidden bg-white border border-gray-200">

            {/* Header com ícone */}
            <div className="px-6 pt-8 pb-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-yellow-50 border border-amber-100 flex items-center justify-center mx-auto mb-5">
                <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Limite de sessões atingido</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                Você já atingiu o número máximo de sessões permitidas no seu plano atual.
                Para criar novas sessões, faça upgrade do seu plano.
              </p>
            </div>

            {/* Informações do plano */}
            <div className="mx-6 mb-6 bg-gray-50 rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Plano atual</span>
                <span className="text-sm font-semibold text-gray-900">{limitModalData.planoAtual}</span>
              </div>
              <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Limite do plano</span>
                <span className="text-sm font-semibold text-gray-900">{limitModalData.limitePlano} {limitModalData.limitePlano === 1 ? 'sessão' : 'sessões'}</span>
              </div>
              <div className="px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sessões em uso</span>
                  <span className="text-sm font-bold text-yellow-700">{limitModalData.quantidadeAtual} / {limitModalData.limitePlano}</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: '100%' }} />
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="px-6 pb-6 flex flex-col gap-2.5">
              <button
                type="button"
                onClick={() => { setLimitModalData(null); onIrParaPagamento?.(); }}
                className="w-full py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Fazer upgrade de plano
              </button>
              <button
                type="button"
                onClick={() => setLimitModalData(null)}
                className="w-full py-3 rounded-xl border border-gray-200 text-gray-600 font-medium text-sm hover:bg-gray-50 transition-colors"
              >
                Fechar
              </button>
            </div>

          </div>
        </div>,
        document.body
      )}

      {/* Modal Mensagens - Chat WhatsApp Style */}
      {messagesModalSessionId !== null && createPortal(
        <div className="fixed inset-0 z-[9999] flex flex-col overflow-hidden bg-white border border-gray-200">
          <div className="flex-1 flex flex-col overflow-hidden bg-white border border-gray-200">
            {/* Header */}
            <div className="flex-shrink-0 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">
                <span className="text-gray-500">Sessão: </span>
                {sessions.find(s => s.id === messagesModalSessionId)?.nomeSessao}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setMessagesModalSessionId(null);
                  setChatSearch('');
                  setChatFilter('all');
                  setChatListPage(0);
                  setSelectedChatPhone(null);
                }}
                className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-200 transition-all"
                title="Fechar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content: Split view de contatos e mensagens */}
            <div className="flex-1 flex overflow-hidden">
              {/* Left: Contacts list */}
              <div className="w-80 border-r flex flex-col bg-white border border-gray-200">
                {/* Search and filters */}
                <div className="flex-shrink-0 border-b p-3 space-y-3 bg-white border border-gray-200">
                  {/* Search */}
                  <div className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      value={chatSearch}
                      onChange={e => setChatSearch(e.target.value)}
                      placeholder="Pesquisar"
                      className="bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none flex-1"
                    />
                  </div>

                  {/* Filters */}
                  <div className="flex gap-2">
                    {[
                      { key: 'all', label: 'Todos' },
                      { key: 'contacts', label: 'Contatos' },
                      { key: 'groups', label: 'Grupos' },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setChatFilter(key as any)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          chatFilter === key
                            ? 'bg-[rgba(34,197,94,0.2)] text-emerald-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {messagesLoading.has(messagesModalSessionId) ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-6 h-6 text-green-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span className="text-sm text-gray-500">Carregando conversa...</span>
                    </div>
                  </div>
                ) : sessionConversations[messagesModalSessionId]?.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center">
                    <p className="text-sm text-gray-400">Nenhuma conversa</p>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto bg-white border border-gray-200">
                    <div className="divide-y divide-gray-200">
                      {(() => {
                        const isGroup = (phone: string) => phone.includes('-');
                        const conversations = sessionConversations[messagesModalSessionId] || [];
                        const sortConversations = (items: ChatConversation[]) => [...items].sort((a, b) => {
                          if (a.hasMessages && !b.hasMessages) return -1;
                          if (!a.hasMessages && b.hasMessages) return 1;
                          if (a.hasMessages && b.hasMessages) {
                            return new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
                          }
                          return (a.name || a.phone).localeCompare(b.name || b.phone);
                        });
                        const contacts = sortConversations(conversations.filter(c => !isGroup(c.phone)));
                        const groups = sortConversations(conversations.filter(c => isGroup(c.phone)));

                        // Aplicar filtros de pesquisa e tipo
                        const filteredSource = chatFilter === 'groups' ? groups : contacts;
                        const filtered = filteredSource.filter(c => {
                          const matchSearch = !chatSearch.trim() ||
                            (c.name || c.phone).toLowerCase().includes(chatSearch.toLowerCase()) ||
                            c.lastMessage.toLowerCase().includes(chatSearch.toLowerCase());
                          return matchSearch;
                        });
                        const totalChatPages = Math.max(1, Math.ceil(filtered.length / CHAT_LIST_PAGE_SIZE));
                        const currentChatPage = Math.min(chatListPage, totalChatPages - 1);
                        const paginated = filtered.slice(currentChatPage * CHAT_LIST_PAGE_SIZE, (currentChatPage + 1) * CHAT_LIST_PAGE_SIZE);

                        return (
                          <>
                            {paginated.map((conversation) => {
                          const isCurrentGroup = isGroup(conversation.phone);

                          let timeDisplay = '';
                          if (conversation.hasMessages && conversation.lastMessageTime) {
                            const lastMsgDate = new Date(conversation.lastMessageTime);
                            const now = new Date();
                            const isToday = lastMsgDate.toDateString() === now.toDateString();
                            const isThisYear = lastMsgDate.getFullYear() === now.getFullYear();
                            timeDisplay = isToday
                              ? lastMsgDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                              : isThisYear
                              ? lastMsgDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
                              : lastMsgDate.toLocaleDateString('pt-BR');
                          }

                          return (
                            <div key={conversation.phone}>
                              <div
                                onClick={() => {
                                  setSelectedChatPhone(conversation.phone);
                                  setReplyingTo(null);
                                }}
                                className={`px-3 py-2 cursor-pointer transition-colors border-l-4 ${
                                  selectedChatPhone === conversation.phone
                                    ? 'bg-emerald-50 border-l-green-500'
                                    : 'bg-transparent border-l-transparent hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-base font-semibold flex-shrink-0 shadow-sm overflow-hidden ${
                                    conversation.photo ? '' : (isCurrentGroup
                                      ? 'bg-gradient-to-br from-slate-500 to-slate-700'
                                      : 'bg-gradient-to-br from-blue-400 to-blue-600')
                                  }`}>
                                    {conversation.photo ? (
                                      <img src={conversation.photo} alt={conversation.name || conversation.phone} className="w-full h-full object-cover" />
                                    ) : isCurrentGroup ? (
                                      <span className="text-sm font-bold">GP</span>
                                    ) : (
                                      (conversation.name || conversation.phone).charAt(0).toUpperCase()
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                      <p className="text-sm font-semibold text-gray-900 truncate">{conversation.name || conversation.phone}</p>
                                      {conversation.hasMessages && timeDisplay && (
                                        <span className="text-xs text-gray-500 flex-shrink-0">{timeDisplay}</span>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-600 truncate">
                                      {conversation.hasMessages ? conversation.lastMessage || '(sem texto)' : <span className="italic text-gray-400">Nenhuma mensagem</span>}
                                    </p>
                                    {(conversation.hasImage || conversation.hasButtons) && (
                                      <p className="text-[10px] text-gray-400 mt-0.5">
                                        {[conversation.hasImage ? 'imagem' : null, conversation.hasButtons ? 'botoes' : null].filter(Boolean).join(' + ')}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                            {filtered.length > CHAT_LIST_PAGE_SIZE && (
                              <div className="px-3 py-3 border-t bg-white border border-gray-200">
                                <div className="flex items-center justify-between gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setChatListPage(prev => Math.max(0, prev - 1))}
                                    disabled={currentChatPage === 0}
                                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 disabled:opacity-40"
                                  >
                                    Anterior
                                  </button>
                                  <span className="text-xs text-gray-500">Página {currentChatPage + 1} de {totalChatPages}</span>
                                  <button
                                    type="button"
                                    onClick={() => setChatListPage(prev => Math.min(totalChatPages - 1, prev + 1))}
                                    disabled={currentChatPage >= totalChatPages - 1}
                                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 disabled:opacity-40"
                                  >
                                    Próxima
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Right: Messages */}
              <div className="flex-1 flex flex-col bg-gradient-to-b from-white via-blue-50 to-white">
                {!selectedChatPhone ? (
                  <div className="flex-1 flex flex-col items-center justify-center gap-3">
                    <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-gray-400 text-sm font-medium">Selecione um contato para ver as mensagens</p>
                  </div>
                ) : (
                  <>
                    {/* Message header */}
                    {(() => {
                      const isCurrentChatGroup = selectedChatPhone && selectedChatPhone.includes('-');
                      const conversation = sessionConversations[messagesModalSessionId]?.find(c => c.phone === selectedChatPhone);

                      return (
                        <div className="flex-shrink-0 border-b px-6 py-4 flex items-center gap-4 shadow-sm bg-white border border-gray-200">
                          <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-semibold text-xl shadow-md flex-shrink-0 overflow-hidden ${
                            conversation?.photo ? '' : (isCurrentChatGroup
                              ? 'bg-gradient-to-br from-purple-400 to-purple-600'
                              : 'bg-gradient-to-br from-blue-400 to-blue-600')
                          }`}>
                            {conversation?.photo ? (
                              <img src={conversation.photo} alt={conversation?.name || selectedChatPhone} className="w-full h-full object-cover" />
                            ) : isCurrentChatGroup ? (
                              <span className="text-base font-bold">GP</span>
                            ) : (
                              (conversation?.name || selectedChatPhone || '').charAt(0).toUpperCase()
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900 text-base">
                              {conversation?.name || 'Conversa'}
                            </p>
                            <p className="text-sm text-gray-500">
                              {isCurrentChatGroup ? `${conversation?.messageCount || 0} mensagens` : selectedChatPhone ? `+${selectedChatPhone.replace(/(\d)(?=(\d{4})+$)/g, '$1 ')}` : ''}
                            </p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-3 bg-gradient-to-b from-blue-50 to-white">
                      {(() => {
                        const currentChatKey = messagesModalSessionId !== null && selectedChatPhone ? `${messagesModalSessionId}:${selectedChatPhone}` : '';
                        const currentMessages = currentChatKey ? (chatMessages[currentChatKey] || []) : [];
                        const loadingCurrentMessages = currentChatKey ? chatMessagesLoading.has(currentChatKey) : false;

                        if (loadingCurrentMessages) {
                          return <div className="flex-1 flex items-center justify-center text-sm text-gray-500">Carregando mensagens...</div>;
                        }

                        if (currentMessages.length === 0) {
                          return <div className="flex-1 flex items-center justify-center text-sm text-gray-400">Nenhuma mensagem nessa conversa</div>;
                        }

                        const convPhoto = messagesModalSessionId !== null
                          ? sessionConversations[messagesModalSessionId]?.find(c => c.phone === selectedChatPhone)?.photo
                          : undefined;

                        return currentMessages.map((msg) => {
                          const isSent = msg.direction === 'outbound';
                          const mediaSrc = msg.mediaUrl
                            ? (msg.mediaUrl.startsWith('/uploads') ? `${API_URL}${msg.mediaUrl}` : msg.mediaUrl)
                            : null;

                          return (
                            <div
                              key={msg.id}
                              className={`flex ${isSent ? 'justify-end' : 'justify-start'} animate-fadeIn group`}
                            >
                              <div className="flex items-end gap-1.5 max-w-[78%]">
                                {!isSent && (
                                  <button
                                    onClick={() => setReplyingTo({ id: msg.id, message: msg.message })}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 rounded-lg"
                                    title="Responder"
                                  >
                                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4" />
                                    </svg>
                                  </button>
                                )}
                                <div
                                  className={`relative px-3 py-2 rounded-lg shadow-sm ${ isSent ? 'bg-[#d9fdd3] text-gray-900 rounded-tr-md' : 'bg-white text-gray-900 rounded-tl-md border border-gray-200' }`}
                                >
                                  <span
                                    className={`absolute top-0 ${isSent ? '-right-1 border-l-[#d9fdd3]' : '-left-1 border-r-white'} w-0 h-0 border-y-[8px] border-y-transparent ${isSent ? 'border-l-[10px]' : 'border-r-[10px]'}`}
                                  />
                                  {msg.quotedMessage && (
                                    <div className={`mb-2 px-3 py-2 rounded-md border-l-4 ${isSent ? 'bg-[#bce7b7] border-[#53bdeb]' : 'bg-gray-50 border-[#53bdeb]'}`}>
                                      <p className="text-xs text-gray-500">Respondendo</p>
                                      <p className="text-sm line-clamp-2 text-gray-700">{msg.quotedMessage}</p>
                                    </div>
                                  )}
                                  {mediaSrc && (() => {
                                    const rawExt = mediaSrc.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
                                    const msgLower = (msg.message || '').toLowerCase();
                                    const isVideo = ['mp4', 'webm', 'mov', 'avi'].includes(rawExt)
                                      || msgLower.includes('vídeo') || msgLower.includes('video');
                                    const isAudio = ['mp3', 'ogg', 'opus', 'wav', 'm4a', 'aac'].includes(rawExt)
                                      || msgLower.includes('áudio') || msgLower.includes('audio');
                                    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(rawExt)
                                      || msgLower.includes('imagem') || msgLower.includes('foto') || msgLower.includes('figurinha');

                                    if (isVideo) return (
                                      <video
                                        src={mediaSrc}
                                        controls
                                        className="rounded-xl mb-2 max-h-64 w-full bg-black"
                                      />
                                    );
                                    if (isAudio) return (
                                      <AudioPlayer src={mediaSrc} isSent={isSent} photo={!isSent ? convPhoto : undefined} />
                                    );
                                    if (isImage) return (
                                      <img
                                        src={mediaSrc}
                                        alt="midia"
                                        className="rounded-xl mb-2 max-h-64 object-cover w-full cursor-pointer"
                                        onClick={() => setLightboxSrc(mediaSrc)}
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                      />
                                    );
                                    return (
                                      <a
                                        href={mediaSrc}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 mb-2 px-3 py-2 bg-white/60 rounded-lg border border-gray-200 text-blue-700 hover:text-blue-700 text-sm font-medium"
                                      >
                                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                        </svg>
                                        Baixar arquivo
                                      </a>
                                    );
                                  })()}
                                  <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{msg.message}</p>
                                  {!!msg.buttons?.length && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {msg.buttons.map((button, index) => (
                                        <span
                                          key={`${msg.id}-${index}`}
                                          className={`px-2.5 py-1 rounded-full text-[11px] border ${isSent ? 'bg-emerald-100 border-emerald-200 text-emerald-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}
                                        >
                                          {button.texto}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  <p className="text-[11px] mt-1 flex items-center justify-end gap-1 text-gray-500">
                                    <span>{new Date(msg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                                    {isSent && (
                                      <svg className="w-3.5 h-3.5 text-[#53bdeb]" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M7.2 13.2 4.5 10.5l-.9.9 3.6 3.6 8.1-8.1-.9-.9z" />
                                        <path d="M10.2 13.2 7.5 10.5l-.9.9 3.6 3.6 8.1-8.1-.9-.9z" />
                                      </svg>
                                    )}
                                  </p>
                                </div>
                                {isSent && (
                                  <button
                                    onClick={() => setReplyingTo({ id: msg.id, message: msg.message })}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-[rgba(34,197,94,0.2)] rounded-lg"
                                    title="Responder"
                                  >
                                    <svg className="w-5 h-5 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Input Message */}
                    <div className="flex-shrink-0 border-t px-4 py-3 bg-white border border-gray-200">
                      {/* Hidden file input */}
                      <input
                        ref={mediaInputRef}
                        type="file"
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
                        className="hidden"
                        onChange={handleMediaSelect}
                      />
                      {/* Reply banner */}
                      {replyingTo && (
                        <div className="mb-2 px-3 py-2 bg-blue-50 border-l-4 border-blue-500 rounded-lg flex items-start justify-between">
                          <div>
                            <p className="text-xs font-semibold text-blue-700">Respondendo</p>
                            <p className="text-sm text-gray-700 line-clamp-2">{replyingTo.message}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setReplyingTo(null)}
                            className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0"
                            title="Cancelar resposta"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                      {/* Media preview */}
                      {mediaToSend && mediaPreview && (
                        <div className="mb-2 flex items-center gap-3 p-2 bg-gray-100 rounded-xl border border-gray-200">
                          {mediaToSend.type.startsWith('image/') && (
                            <img src={mediaPreview} alt="preview" className="h-16 w-16 object-cover rounded-lg flex-shrink-0" />
                          )}
                          {mediaToSend.type.startsWith('video/') && (
                            <video src={mediaPreview} className="h-16 w-16 object-cover rounded-lg flex-shrink-0" />
                          )}
                          {mediaToSend.type.startsWith('audio/') && (
                            <div className="flex items-center gap-2 flex-shrink-0 px-2 py-1 bg-[rgba(34,197,94,0.2)] rounded-lg">
                              <svg className="w-5 h-5 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                              </svg>
                              <span className="text-xs text-emerald-700 font-medium">Áudio</span>
                            </div>
                          )}
                          {!mediaToSend.type.startsWith('image/') && !mediaToSend.type.startsWith('video/') && !mediaToSend.type.startsWith('audio/') && (
                            <div className="flex items-center gap-2 flex-shrink-0 px-2 py-1 bg-[rgba(59,130,246,0.2)] rounded-lg">
                              <svg className="w-5 h-5 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-900 truncate">{mediaToSend.name}</p>
                            <p className="text-[11px] text-gray-500">{(mediaToSend.size / 1024 / 1024).toFixed(1)} MB</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (mediaPreview) URL.revokeObjectURL(mediaPreview);
                              setMediaToSend(null);
                              setMediaPreview(null);
                            }}
                            className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                            title="Remover mídia"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                      <div className="flex items-end gap-2">
                        {/* Attachment button */}
                        <button
                          type="button"
                          onClick={() => mediaInputRef.current?.click()}
                          className="flex-shrink-0 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 rounded-full transition-all"
                          title="Anexar arquivo"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                        </button>
                        <input
                          type="text"
                          value={messageInput}
                          onChange={(e) => setMessageInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (mediaToSend) { handleSendMedia(); }
                              else if (messageInput.trim()) { handleSendMessage(); }
                            }
                          }}
                          placeholder={mediaToSend ? 'Legenda (opcional)...' : 'Escreva uma mensagem...'}
                          className="flex-1 bg-gray-100 border border-gray-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
                        />
                        <button
                          type="button"
                          onClick={mediaToSend ? handleSendMedia : handleSendMessage}
                          disabled={!mediaToSend && !messageInput.trim()}
                          className="w-10 h-10 flex items-center justify-center bg-[#00a884] hover:bg-[#02916f] disabled:opacity-40 text-white rounded-full transition-all flex-shrink-0 shadow-sm"
                          title="Enviar"
                        >
                          <svg className="w-5 h-5 -ml-0.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3.4 20.4 21.8 12 3.4 3.6l.1 6.4 12.3 2-12.3 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

          </div>
        </div>,
        document.body
      )}

      {/* Lightbox */}
      {lightboxSrc && createPortal(
      <div
        className="fixed inset-0 z-[99999] flex flex-col bg-black/95"
        onClick={(e) => { if (e.target === e.currentTarget) setLightboxSrc(null); }}
      >
        {/* Toolbar */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-black/60 backdrop-blur-sm">
          <span className="text-gray-600 text-sm select-none">Visualizar imagem</span>
          <div className="flex items-center gap-2">
            <a
              href={lightboxSrc}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-100 text-gray-900 rounded-full text-sm font-medium transition-all"
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Baixar
            </a>
            <button
              type="button"
              onClick={() => setLightboxSrc(null)}
              className="flex items-center justify-center w-9 h-9 bg-gray-100 hover:bg-gray-100 text-gray-900 rounded-full transition-all"
              title="Fechar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        {/* Image */}
        <div
          className="flex-1 flex items-center justify-center p-4 overflow-auto"
          onClick={() => setLightboxSrc(null)}
        >
          <img
            src={lightboxSrc}
            alt="imagem"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none"
            onClick={(e) => e.stopPropagation()}
            style={{ cursor: 'default' }}
          />
        </div>
      </div>,
      document.body
    )}
    </div>
  );
}
